import React from 'react';
import { Plus, Star, User, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import KanbanBoardWrapper from './KanbanBoardWrapper';

const getMemberPhoto = (name?: any): string | null => {
  if (!name || typeof name !== 'string') return null;
  const lower = name.toLowerCase();
  if (lower.includes('joão') || lower.includes('joao')) {
    return 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('maria')) {
    return 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('pedro')) {
    return 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('ana')) {
    return 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('lucas')) {
    return 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('carla')) {
    return 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('carlos')) {
    return 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80';
  }
  if (lower.includes('marcos')) {
    return 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&auto=format&fit=crop&q=80';
  }
  // Deterministic fallback based on char codes
  const fallbacks = [
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=100&auto=format&fit=crop&q=80'
  ];
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return fallbacks[sum % fallbacks.length];
};

export interface KanbanColumn {
  id: string;
  label: string;
  badge: string;
}

export interface KanbanItem {
  id: string | number;
  title: string;
  subtitle: string; 
  owner?: string;
  priority: string;
  progress: number;
  dueDate?: string;
  budgetOrValue?: string | number;
  budgetLabel?: string;
  isStarred?: boolean;
  status: string; 
  raw: any;
  badges?: { text: string; colorClass?: string }[];
  customMetrics?: { label: string; value: string | number; colorClass?: string }[];
}

interface BoardKanbanProps {
  columns: KanbanColumn[];
  items: KanbanItem[];
  onDrop: (e: React.DragEvent, colId: string) => void;
  onItemClick: (item: any) => void;
  onToggleFavorite?: (id: number) => void;
  onAddClick?: (colId: string) => void;
  emptyMessage?: string;
  disableLayoutAnimation?: boolean;
}

export default function BoardKanban({
  columns,
  items,
  onDrop,
  onItemClick,
  onToggleFavorite,
  onAddClick,
  emptyMessage = "Vazio",
  disableLayoutAnimation = false
}: BoardKanbanProps) {
  const handleDragStart = (e: React.DragEvent, id: string | number) => {
    (window as any).__draggedItemId = id;
    e.dataTransfer.setData('text/plain', id.toString());
    e.dataTransfer.setData('itemId', id.toString());
    e.dataTransfer.setData('projectId', id.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  return (
    <KanbanBoardWrapper>
      {columns.map((col) => {
        const columnItems = items.filter(p => p.status === col.id);

        return (
          <div 
            key={col.id}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => onDrop(e, col.id)}
            className="flex-shrink-0 w-72 bg-[#FAFAFA]/50 rounded-2xl p-3 border border-neutral-100 flex flex-col min-h-[520px] snap-start"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-3 px-1 text-left">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${col.badge}`}>
                  {col.label}
                </span>
                <span className="text-[10px] font-extrabold text-neutral-400">({columnItems.length})</span>
              </div>
              {onAddClick && (
                <button 
                  onClick={() => onAddClick(col.id)}
                  className="w-5 h-5 rounded-md hover:bg-neutral-100/80 text-neutral-400 hover:text-neutral-900 flex items-center justify-center transition-all cursor-pointer border border-transparent hover:border-neutral-200/50"
                  title={`Adicionar item em ${col.label}`}
                >
                  <Plus size={12} />
                </button>
              )}
            </div>

            {/* Cards stack */}
            <div className="flex flex-col gap-2.5 overflow-y-auto max-h-[580px] pr-1 scrollbar-none flex-1">
              <AnimatePresence initial={false}>
                {columnItems.length > 0 ? (
                  columnItems.map((p) => {
                    return (
                      <motion.div 
                        key={p.id}
                        layout={!disableLayoutAnimation}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        draggable
                        onDragStart={(e: any) => handleDragStart(e as any, p.id)}
                        className="bg-white p-4 rounded-xl border border-neutral-200/50 hover:border-neutral-300 shadow-[0_1px_3px_rgba(0,0,0,0.01)] hover:shadow-sm cursor-grab active:cursor-grabbing transition-all text-left relative group"
                      >
                        {/* Starred Favorite */}
                        {onToggleFavorite && (
                          <button 
                            onClick={(e) => { e.stopPropagation(); onToggleFavorite(Number(p.id)); }}
                            className="absolute top-3 right-3 text-neutral-300 hover:text-amber-500 transition-colors"
                          >
                            <Star size={13} fill={p.isStarred ? "currentColor" : "none"} className={p.isStarred ? "text-amber-500" : ""} />
                          </button>
                        )}

                        {/* Client & Title with Visual Identity Logo */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-grow min-w-0">
                            <span className="text-[9px] font-bold text-neutral-400 block tracking-wide uppercase truncate">
                              {p.subtitle || 'S/N'}
                            </span>
                            <h4 
                              onClick={() => onItemClick(p.raw)}
                              className="text-xs font-black text-neutral-900 mt-1 hover:underline cursor-pointer tracking-tight"
                            >
                              {p.title}
                            </h4>
                          </div>
                          {(p.raw?.logoUrl || p.title) && (
                            <div className="w-7 h-7 rounded-lg border border-neutral-200/60 flex items-center justify-center overflow-hidden bg-white shrink-0 shadow-sm mt-0.5">
                              {p.raw?.logoUrl ? (
                                <img 
                                  src={p.raw.logoUrl} 
                                  alt={p.title} 
                                  referrerPolicy="no-referrer"
                                  className="w-full h-full object-contain p-0.5" 
                                />
                              ) : (
                                <div className="w-full h-full bg-[#111111] text-white flex items-center justify-center font-bold text-[10px]">
                                  {p.title ? p.title.trim().charAt(0).toUpperCase() : '?'}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-1.5 mt-2">
                          <div className="w-4.5 h-4.5 rounded-full bg-slate-100 border border-slate-200 text-slate-800 flex items-center justify-center text-[7px] font-bold overflow-hidden shrink-0 shadow-3xs">
                            {getMemberPhoto(p.owner) ? (
                              <img src={getMemberPhoto(p.owner)!} alt={p.owner} className="w-full h-full object-cover animate-in fade-in duration-200" referrerPolicy="no-referrer" />
                            ) : (
                              <User size={8} />
                            )}
                          </div>
                          <span className="text-[10px] text-slate-600 font-semibold">{p.owner || 'Sem responsável'}</span>
                        </div>

                        {/* Priority Status indicator and Custom Badges */}
                        <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                          <span className={`text-[8px] font-black tracking-wider uppercase px-1.5 py-0.5 rounded-md ${
                            p.priority === 'Alta' ? 'bg-red-50 text-red-700' : 'bg-neutral-50 text-neutral-600'
                          }`}>
                            {p.priority || 'Normal'}
                          </span>
                          
                          {p.dueDate && (
                            <span className="text-[9px] font-semibold text-neutral-400 flex items-center gap-1">
                              <Calendar size={10} />
                              {new Date(p.dueDate).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                            </span>
                          )}

                          {p.badges && p.badges.map((b, i) => (
                            <span key={i} className={`text-[8px] font-black tracking-wider uppercase px-1.5 py-0.5 rounded-md ${b.colorClass || 'bg-neutral-50 text-neutral-600'}`}>
                              {b.text}
                            </span>
                          ))}
                        </div>

                        {/* Interactive Progress Bar */}
                        <div className="mt-3.5">
                          <div className="flex items-center justify-between text-[10px] text-neutral-500 font-bold mb-1">
                            <span>Progresso</span>
                            <span>{p.progress}%</span>
                          </div>
                          <div className="w-full h-1 bg-neutral-100 rounded-full overflow-hidden">
                            <div className="h-full bg-neutral-950 rounded-full" style={{ width: `${p.progress}%` }} />
                          </div>
                        </div>

                        {/* Custom Metrics */}
                        {p.customMetrics && p.customMetrics.length > 0 && (
                          <div className="mt-4 pt-3 border-t border-neutral-100 grid grid-cols-2 gap-2">
                            {p.customMetrics.map((metric, i) => (
                              <div key={i} className="flex flex-col gap-0.5">
                                <span className="text-[8px] font-bold text-neutral-400 uppercase tracking-wide">{metric.label}</span>
                                <span className={`text-[11px] font-black ${metric.colorClass || 'text-neutral-900'}`}>{metric.value}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Team initials display / Values */}
                        {p.budgetOrValue !== undefined && (!p.customMetrics || p.customMetrics.length === 0) && (
                          <div className="mt-4 pt-3 border-t border-neutral-100 flex items-center justify-between">
                            <span className="text-[9px] font-bold text-neutral-400 uppercase">{p.budgetLabel || 'Valor'}</span>
                            <span className="text-xs font-black text-neutral-900">
                              {typeof p.budgetOrValue === 'number' 
                                ? `R$ ${p.budgetOrValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                : p.budgetOrValue}
                            </span>
                          </div>
                        )}
                      </motion.div>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 border border-dashed border-neutral-200/50 rounded-xl text-center">
                    <span className="text-[9px] font-black tracking-widest text-neutral-300 uppercase">{emptyMessage}</span>
                  </div>
                )}
              </AnimatePresence>
            </div>
          </div>
        );
      })}
    </KanbanBoardWrapper>
  );
}
